/**
 * Calendar date-selection / rating sheets — reuse the same React Query menu cache as MenuFlow.
 * Avoids a fresh `GET menus` on every popup open when data was fetched recently (5 min staleTime).
 */
import type { QueryClient } from '@tanstack/react-query';
import { fetchMenu, type FetchMenuResult, resolveFoodTypeCategoryTag } from '../menu/api';
import { MENU_CACHE_GC_MS, MENU_CACHE_STALE_MS } from '../menu/hooks';
import { isPastMenuDate, PAST_DATE_MENU_STALE_MS } from '../menu/cache';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';
import type { MenuBuilder } from '../menu/types';
import { menuKeys } from '../menu/keys';
import { isMenuInvalidationAllowedForDate } from '../menu/cache';

export type CalendarDateSelectionFoodItem = {
  id: number;
  name: string;
  a_name: string;
  description: string;
  a_description: string;
  attachment: string;
  food_type_category: 'meal' | 'snack';
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  quantity?: number;
  menu_builder_id?: number;
  my_rating: number;
  comment?: string;
};

function resolveBuilderId(builder: MenuBuilder): number {
  return Number(builder.food_type_id ?? (builder as { id?: number }).id ?? 0);
}

function parseCalendarFoodItem(item: Record<string, unknown>, builder: MenuBuilder): CalendarDateSelectionFoodItem {
  const raw_rating = item.my_rating ?? item.rating ?? item.user_rating ?? 0;
  const my_rating = Math.max(0, Math.min(5, Math.round(Number(raw_rating) || 0)));
  return {
    id: Number(item.id) || 0,
    name: String(item.name ?? ''),
    a_name: String(item.a_name ?? ''),
    description: String(item.description ?? ''),
    a_description: String(item.a_description ?? ''),
    attachment: String(item.attachment ?? ''),
    food_type_category: resolveFoodTypeCategoryTag(item as Parameters<typeof resolveFoodTypeCategoryTag>[0], builder),
    calories: parseFloat(String(item.calories ?? 0)) || 0,
    protein: parseFloat(String(item.protein ?? 0)) || 0,
    carbs: parseFloat(String(item.carbs ?? 0)) || 0,
    fats: parseFloat(String(item.fats ?? 0)) || 0,
    quantity: Number(item.quantity) || 1,
    menu_builder_id: resolveBuilderId(builder),
    my_rating,
    comment: typeof item.comment === 'string' ? item.comment : undefined,
  };
}

function isSelectedMenuRow(item: Record<string, unknown>): boolean {
  return Boolean(item.is_selected) || Number(item.quantity ?? 0) > 0;
}

export function extractCalendarSelectedFoodItems(menu_builders: MenuBuilder[]): CalendarDateSelectionFoodItem[] {
  const items: CalendarDateSelectionFoodItem[] = [];
  for (const builder of menu_builders) {
    for (const row of builder.type_data ?? []) {
      const item = row as unknown as Record<string, unknown>;
      if (isSelectedMenuRow(item)) {
        items.push(parseCalendarFoodItem(item, builder));
      }
      const replaceables = (item.replaceable_menu_items as Record<string, unknown>[] | undefined) ?? [];
      for (const replaceable of replaceables) {
        if (isSelectedMenuRow(replaceable)) {
          items.push(parseCalendarFoodItem(replaceable, builder));
        }
      }
    }
  }
  return items;
}

function menuQueryKey(customer_id: number, date_ascii: string, is_double_box: boolean) {
  return menuKeys.day(customer_id, date_ascii, is_double_box);
}

export function readCalendarDateSelectionMenuFromCache(
  query_client: QueryClient,
  customer_id: number,
  date: string,
  is_double_box = false,
): CalendarDateSelectionFoodItem[] | null {
  const date_ascii = ensureAsciiDateString(date) || date;
  const cached = query_client.getQueryData<FetchMenuResult>(menuQueryKey(customer_id, date_ascii, is_double_box));
  if (cached?.menu_builders == null) {
    return null;
  }
  return extractCalendarSelectedFoodItems(cached.menu_builders);
}

/** Uses React Query cache (5 min fresh) — same key as `useMenuForDateQuery`. */
export async function fetchCalendarDateSelectionMenu(
  query_client: QueryClient,
  customer_id: number,
  date: string,
  is_double_box = false,
): Promise<CalendarDateSelectionFoodItem[]> {
  if (!customer_id || !date) {
    return [];
  }
  const date_ascii = ensureAsciiDateString(date) || date;
  const is_past = isPastMenuDate(date_ascii);
  const result = await query_client.fetchQuery({
    queryKey: menuQueryKey(customer_id, date_ascii, is_double_box),
    queryFn: () => fetchMenu(customer_id, date_ascii, is_double_box),
    staleTime: is_past ? PAST_DATE_MENU_STALE_MS : MENU_CACHE_STALE_MS,
    gcTime: is_past ? PAST_DATE_MENU_STALE_MS : MENU_CACHE_GC_MS,
  });
  return extractCalendarSelectedFoodItems(result.menu_builders ?? []);
}

export function invalidateCalendarDateSelectionMenuCache(
  query_client: QueryClient,
  customer_id: number,
  date: string,
): void {
  const date_ascii = ensureAsciiDateString(date) || date;
  if (!isMenuInvalidationAllowedForDate(date_ascii)) {
    return;
  }
  void query_client.invalidateQueries({ queryKey: menuQueryKey(customer_id, date_ascii, false) });
  void query_client.invalidateQueries({ queryKey: menuQueryKey(customer_id, date_ascii, true) });
}
